import React, { FC, useEffect, useState } from 'react';
import { AttachFileIcon, BackIcon, SendLineIcon } from 'assets/icons';
import { CustomInput, If, ImagePreview } from 'components';
import { EstimateTimelineEvent, EstimateTimelineEventType } from 'containers/estimates/estimates.model';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { useCreateEstimateNoteMutation } from 'containers/estimates/api';
import EstimateTimelineSection from 'containers/estimates/components/EstimateTimelineSection';
import { VoidFn } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { EstimatesCleverTapEvents } from 'containers/estimates/events';
import CreateEditEstimateNoteModal from 'containers/estimates/components/CreateEditEstimateNoteModal';

interface EstimateTimelinePanelProps {
  onBackClick: VoidFn;
  estimateTimeline: EstimateTimelineEvent[];
  estimateId: string;
  teamId: number;
  getEstimateTimeline: VoidFn;
  onEditNote: (event: EstimateTimelineEvent) => void;
  onDeleteNote: (event: EstimateTimelineEvent) => void;
  notesInput: string;
  setNotesInput: (note: string) => void;
  isEditAllowed: boolean;
}

const historyTabs = [
  {
    label: 'All',
    value: 'all',
    filters: []
  },
  {
    label: 'Notes',
    value: 'notes',
    filters: [EstimateTimelineEventType.Note]
  },
  {
    label: 'Actions',
    value: 'actions',
    filters: [EstimateTimelineEventType.Action]
  }
];

const EstimateTimelinePanel: FC<EstimateTimelinePanelProps> = ({
  onBackClick,
  estimateTimeline,
  estimateId,
  teamId,
  getEstimateTimeline,
  onEditNote,
  onDeleteNote,
  notesInput,
  setNotesInput,
  isEditAllowed
}) => {
  const [currentTab, setCurrentTab] = useState(historyTabs[0]);
  const [filteredTimeline, setFilteredTimeline] = useState<EstimateTimelineEvent[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState(null);
  const [isCreateNoteModalOpen, setIsCreateNoteModalOpen] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [currentNote, setCurrentNote] = useState<EstimateTimelineEvent | null>(null);

  const [createNoteMutation, createNoteState] = useCreateEstimateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;

  useEffect(() => {
    if (estimateTimeline) {
      setFilteredTimeline(estimateTimeline);
      setCurrentTab(historyTabs[0]);
      setIsLoading(false);
    }
  }, [estimateTimeline]);

  useEffect(() => {
    if (!isCreateNoteLoading && isCreateNoteSuccess) {
      getEstimateTimeline();
    }
  }, [isCreateNoteSuccess, isCreateNoteLoading]);

  const handleTabChange = tab => {
    if (tab.value === currentTab.value) return;
    setCurrentTab(tab);
    if (tab.value === 'all') setFilteredTimeline(estimateTimeline);
    else setFilteredTimeline(estimateTimeline.filter(event => tab.filters.includes(event.type)));
  };

  const handleAnalyticEventForNotes = () => {
    logAnalyticEvent(EstimatesCleverTapEvents.webEnterNotesTextBoxClicked);
  };

  const handleOpenCreateNoteModal = () => {
    setIsCreateNoteModalOpen(true);
    logAnalyticEvent(EstimatesCleverTapEvents.webCreateEstimateNotePage);
  };

  const handleCloseCreateNoteModal = () => {
    setIsCreateNoteModalOpen(false);
  };

  const handleCreateNote = async () => {
    if (!notesInput.trim()) {
      return;
    }

    try {
      await createNoteMutation({
        estimateId,
        teamId,
        comment: notesInput.trim()
      }).unwrap();
      setNotesInput('');
    } catch (error) {
      //
    }
  };

  const closePreview = () => {
    setShowImageViewer(false);
    setCurrentImage(0);
    setCurrentNote(null);
  };

  const handleImageClick = (index: number, event: EstimateTimelineEvent) => {
    setCurrentNote(event);
    setCurrentImage(index);
    setShowImageViewer(true);
    logAnalyticEvent(EstimatesCleverTapEvents.webImageViewerOpened, {
      source: 'estimate-timeline-panel'
    });
  };

  // eslint-disable-next-line react/no-multi-comp
  const NotesInputSuffix = () => (
    <div className="flex items-center gap-x-4">
      <AttachFileIcon className="shrink-0 cursor-pointer" onClick={handleOpenCreateNoteModal} />
      <SendLineIcon className="shrink-0 cursor-pointer" width={18} height={18} onClick={handleCreateNote} />
    </div>
  );

  return (
    <div className="h-full w-96">
      <If condition={isLoading}>
        <div className="flex h-full w-96 items-center justify-center">
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </div>
      </If>
      {!isLoading && (
        <div className="flex h-full flex-col">
          <div className="flex items-center py-4">
            <BackIcon className="mx-4 cursor-pointer" id="estimate-details-panel-back-arrow" onClick={onBackClick} />
            <div className="text-17px font-semibold text-headingGray">History</div>
          </div>
          <div className="flex h-full flex-col">
            <div className="flex gap-x-2.5 border border-secondaryBtn p-4">
              {historyTabs.map(tab => (
                <div
                  key={`estimate-history-${tab.value}-tab`}
                  className={`flex w-[60px] cursor-pointer justify-center rounded-full border py-1.5 text-px13
              text-heading 
                ${
                  currentTab.value === tab.value
                    ? 'border-primaryBtn bg-secondaryBtn font-semibold'
                    : 'border-gray-1 bg-white'
                }`}
                  onClick={() => handleTabChange(tab)}>
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="relative flex h-full flex-col pb-[240px]">
              <EstimateTimelineSection
                estimateTimeline={filteredTimeline}
                onEditNoteClick={onEditNote}
                onDeleteNoteClick={onDeleteNote}
                hasImageViewer={true}
                showPreview={handleImageClick}
                timelineId="estimates-history-timeline"
                currentActionsPopoverId={currentActionsPopoverId}
                setCurrentActionsPopoverId={setCurrentActionsPopoverId}
                isEditAllowed={isEditAllowed}
              />
              <div className="fixed bottom-0 w-[383px]">
                <div className="mb-0.5 w-full border border-borderGray py-6 px-4">
                  <CustomInput
                    id="estimate-history-notes-input"
                    value={notesInput}
                    onChange={setNotesInput}
                    hasSuffix={true}
                    showSuffixAlways={true}
                    suffix={<NotesInputSuffix />}
                    className="h-12 rounded-md border border-borderGray bg-transparent pr-[72px]
                          placeholder:font-semibold focus:outline-none"
                    suffixStyle="right-4 top-4"
                    placeholder="Enter note"
                    onClick={handleAnalyticEventForNotes}
                    showRemainingChar={true}
                    maxLength={1000}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isCreateNoteModalOpen && (
        <CreateEditEstimateNoteModal
          defaultNote={notesInput}
          onClose={handleCloseCreateNoteModal}
          isEdit={false}
          currentNote={null}
          teamId={teamId}
          estimateId={estimateId}
          getEstimateTimeline={getEstimateTimeline}
          setNotesInput={setNotesInput}
        />
      )}
      {showImageViewer && currentNote && (
        <ImagePreview onClose={closePreview} images={currentNote.attachments} selectedIndex={currentImage} />
      )}
    </div>
  );
};

export default EstimateTimelinePanel;
